import classNames from 'classnames';
import useMenu from '../../../hooks/menu/useMenu';
import usePageHeader from '../../../hooks/navigation/usePageHeader';
import { useMenuCategoryList } from '../category-list/MenuCategoryListProvider';
import { MenuInterface } from '../../../services/exports/Interfaces';
import SearchInput from './SearchInput/SearchInput';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import WorkScheduleHelpers from '../../../services/helpers/WorkScheduleHelpers';

interface Props {
  className?: string;
}

export default function MenuCategorySidebar(props: Props) {
  const { className } = props;
  const { menus, selectedMenu, setSelectedMenu } = useMenu();
  const { filteredCategories, focusedCategoryId, jumpToCategory } = useMenuCategoryList();
  const { pageHeaderHeight } = usePageHeader();
  const { formatScheduleUnit } = useAppMethods();

  const menuItems = (menus?.toArray() ?? []) as MenuInterface[];

  const renderMenuSchedule = (menu: MenuInterface) => {
    if (!menu.schedule?.length) {
      return null;
    }

    const label = WorkScheduleHelpers.formatSchedule(
      menu.schedule,
      (item) => `${formatScheduleUnit(item.open)} – ${formatScheduleUnit(item.close)}`,
      (item) =>
        `${formatScheduleUnit(item.open, { day: true, time: true })} – ${formatScheduleUnit(item.close, { day: true, time: true })}`,
    );

    if (!label) {
      return null;
    }

    return <span className={menuScheduleLabel}>{label}</span>;
  };

  return (
    <aside
      className={classNames(sidebarContainer, className)}
      style={{
        top: pageHeaderHeight.desktop,
        maxHeight: `calc(100vh - ${pageHeaderHeight.desktop}px)`,
      }}
    >
      <SearchInput className="mb-md" />
      {menuItems.length > 1 && (
        <div className="mb-md">
          {menuItems.map((menu) => {
            const isMenuActive = selectedMenu?.id === menu.id;

            return (
              <button
                key={menu.id}
                onClick={() => setSelectedMenu(menu)}
                className={classNames(menuSelectorItem, {
                  [menuSelectorItemActive]: isMenuActive,
                  [menuSelectorItemInactive]: !isMenuActive,
                })}
              >
                <span className="block font-medium text-sm">{menu.name}</span>
                {renderMenuSchedule(menu)}
              </button>
            );
          })}
          <div className={divider} />
        </div>
      )}

      <nav className={categoryList}>
        {filteredCategories.map((category) => {
          const isActive = focusedCategoryId === category.id;

          return (
            <button
              key={category.id}
              onClick={() => jumpToCategory(category.id)}
              className={classNames(categoryItem, {
                [categoryItemActive]: isActive,
                [categoryItemInactive]: !isActive,
              })}
            >
              {category.name}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

const sidebarContainer = 'hidden lg:block w-56 shrink-0 sticky self-start py-md overflow-y-auto scrollbar-hide';
const categoryList = 'flex flex-col space-y-2xs';
const categoryItem = 'w-full text-left px-sm py-xs rounded-lg text-sm transition-all duration-200 flex items-center';
const categoryItemActive = 'font-medium text-brand-primary bg-muted border-l-[3px] border-brand-primary pl-[9px]';
const categoryItemInactive = 'text-muted-foreground hover:text-brand-text-primary hover:bg-brand-background-secondary';
const menuSelectorItem = 'w-full text-left px-sm py-xs rounded-lg transition-colors';
const menuScheduleLabel = 'block text-[11px] text-muted-foreground';
const menuSelectorItemActive = 'bg-brand-primary/10 text-brand-primary font-semibold';
const menuSelectorItemInactive =
  'text-muted-foreground hover:text-brand-text-primary hover:bg-brand-background-secondary';
const divider = 'h-[1px] bg-brand-border mt-sm mb-sm';
